import EditIcon from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, IconButton, TableCell, TableRow, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent, useCallback, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { type BaseRequest, ChangesSource, type NurseFullRole, type OpenShiftRequest, ShiftType } from "../../utils/models";
import { MAX_OPEN_SHIFT_DURATION_HOURS } from "../../utils/openShiftDuration";
import { getRoleGroups, requestShiftOptionsRole } from "../../utils/roles";
import { countNurseResponses, hasNurseResponse } from "../../utils/utilFunctions";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { EditTimePopover } from "../EditTimePopover";
import { InitiateRoboCallButton } from "../InitiateRoboCallButton/InitiateRoboCallButton";
import { useUpdateRequestTime } from "../RequestData/hooks/useUpdateRequestTime";
import { RequestMenu } from "../RequestData/RequestMenu";
import { MessageWorkingFilterEnum } from "../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { PostToAgencyControl } from "../staffing/PostToAgencyControl";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";
import { getStaffTableColumnWidths } from "./utils";

type RequestsTableBodyProps = { date: DateTime; requests: OpenShiftRequest[] };

export const RequestsTableBody: FC<RequestsTableBodyProps> = ({ date, requests }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo, isHospitalView } = useDepartmentFlags();
	const showHospitalView = isHospitalView || isHospitalDemo;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const columnWidths = getStaffTableColumnWidths(showHospitalView);
	const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [actionRequest, setActionRequest] = useState<OpenShiftRequest | null>(null);
	const [showMessageBroadcast, setShowMessageBroadcast] = useState<BaseRequest | null>(null);
	const [editingTime, setEditingTime] = useState<{ nurseId?: string; requestId?: string; element: HTMLElement | null } | null>(null);
	const [selectedRequest, setSelectedRequest] = useState<OpenShiftRequest | null>(null);
	const { handleRequestTimeChange } = useUpdateRequestTime();

	const handleMessageBroadcastClick = useCallback((request: BaseRequest) => {
		setShowMessageBroadcast(request);
	}, []);

	const handleMessageBroadcastClose = useCallback(() => {
		setShowMessageBroadcast(null);
	}, []);

	const handleTimePostedShiftChange = async (requestId: string, newStartTime: DateTime, newEndTime: DateTime): Promise<void> => {
		const request: OpenShiftRequest | undefined = requests.find((r) => r.id === requestId);

		if (!request) return;

		await handleRequestTimeChange(request, newStartTime, newEndTime, ChangesSource.DaySchedule);

		setEditingTime(null);
	};

	const handleActionsRequestOpen = useCallback((event: MouseEvent<HTMLElement>, request: OpenShiftRequest): void => {
		event.stopPropagation();
		setActionsMenuAnchorEl(event.currentTarget);
		setActionRequest(request);
	}, []);

	const handleRequestRowClick = useCallback(
		(request: OpenShiftRequest) => {
			const hasResponses = hasNurseResponse(request);
			if (hasResponses) {
				track("day_view_standard_open_shift_request_click", {
					request_id: request.id,
					has_responses: true,
				});
				setSelectedRequest(request);
			}
		},
		[track],
	);

	const handleDialogClose = useCallback(() => {
		setSelectedRequest(null);
	}, []);

	const formatShiftTotal = (startTime: DateTime, endTime: DateTime): string => {
		const diffInMinutes = endTime.diff(startTime, "minute").minutes;
		const hours = Math.floor(diffInMinutes / 60);
		const minutes = diffInMinutes % 60;

		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} h`;
	};

	return (
		<>
			{showMessageBroadcast && (
				<NewBroadcastMessageModal
					initialSmsMessage={getShiftPickupMessage(
						showMessageBroadcast.message.date,
						showMessageBroadcast.message.shift_start,
						showMessageBroadcast.message.shift_end,
					)}
					addAppLink={true}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
					nurseIdsToIgnore={showMessageBroadcast.message.accepted_nurses}
					jobId={showMessageBroadcast.job_id}
					initialRolesToSelect={showMessageBroadcast.message.roles.map((r) => r as NurseFullRole)}
					onClose={handleMessageBroadcastClose}
					initialFilters={
						showMessageBroadcast.message.date
							? [
									{
										workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
										dateFilter: showMessageBroadcast.message.date,
										timeFilter: {
											start: showMessageBroadcast.message.shift_start,
											end: showMessageBroadcast.message.shift_end,
										},
									},
								]
							: undefined
					}
				/>
			)}
			{requests.map((requestItem) => {
				const responseCount = countNurseResponses(requestItem);
				const hasResponses = responseCount > 0;

				return (
					<Fragment key={`request-${requestItem.id}`}>
						<TableRow
							onClick={() => handleRequestRowClick(requestItem)}
							sx={{
								"&:hover": {
									bgcolor: "inherit",
									"& .delete-button": {
										opacity: 0,
									},
								},
								"&:hover .edit-icon": {
									opacity: 1,
								},
								position: "relative",
								marginTop: 0,
								"& td": {
									py: 0.25,
									fontSize: "0.875rem",
									color: "primary.dark",
									border: "none",
									px: "1.125rem",
									bgcolor: "white",
									borderBottom: "2px solid #E7E9EF",
									textAlign: "left",
									height: "32px",
									lineHeight: "22px",
									verticalAlign: "middle",
									backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
								},
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
								cursor: hasResponses ? "pointer" : "default",
								height: "37px",
							}}
						>
							{/*roles*/}
							<TableCell sx={{ width: columnWidths.role.width, minWidth: columnWidths.role.minWidth, textAlign: "left", verticalAlign: "middle" }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										maxWidth: "100px",
										flexWrap: "wrap",
										justifyContent: "flex-start",
									}}
								>
									{jobManagementEnabled && requestItem.job_id ? (
										<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "primary.dark" }}>
											{chosenDepartment.jobs?.find((j) => j.id === requestItem.job_id)?.displayName ?? "--"}
										</Typography>
									) : (
										getRoleGroups(requestItem.message.roles as NurseFullRole[], chosenDepartment).map((roleGroupName) => (
											<Box
												key={roleGroupName.charAt(0).toUpperCase() + roleGroupName.slice(1)}
												sx={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<ColorfulRole roleGroup={roleGroupName.charAt(0).toUpperCase() + roleGroupName.slice(1)} />
											</Box>
										))
									)}
								</Box>
							</TableCell>
							{/* Job */}
							<TableCell
								sx={{
									width: columnWidths.job.width,
									minWidth: columnWidths.job.minWidth,
									verticalAlign: "middle",
									pl: 2,
								}}
							>
								<Typography sx={{ fontSize: "13px", color: "primary.dark" }}>--</Typography>
							</TableCell>
							{/* Name */}
							<TableCell
								sx={{
									width: columnWidths.name.width,
									minWidth: columnWidths.name.minWidth,
									textAlign: "left",
									pl: 2,
									verticalAlign: "middle",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										whiteSpace: "nowrap",
										justifyContent: "flex-start",
									}}
								>
									<Typography sx={{ fontSize: "14px" }}>{`${requestItem.needed_nurses - requestItem.message.accepted_nurses.length} Posted Shift`}</Typography>
									{/* Self-gating: renders the posted chip only once posted (and the scheduled-post lock before
									    that), so it needs no `&&` guard. The surrounding `gap: 1` plus the chip's own `ml: 1`
									    matches the Requests tab's identical container, so the two read the same. */}
									<PostToAgencyControl request={requestItem} variant="chip" />
								</Box>
							</TableCell>
							{/* Shift */}
							<TableCell
								sx={{
									width: columnWidths.shift.width,
									minWidth: columnWidths.shift.minWidth,
									textAlign: "left",
									verticalAlign: "middle",
								}}
							>
								<Box sx={{ display: "flex" }}>
									<Box
										sx={{
											alignItems: "center",
											gap: 1,
											color: "primary.dark", // override the isRequest color
											position: "relative",
											"&:hover .edit-icon": {
												opacity: 1,
											},
											display: "flex",
											minWidth: "180px",
										}}
									>
										<Typography sx={{ fontSize: "14px", opacity: 0.5 }}>
											{requestItem.message.shift_start.toFormat("h:mm a")} - {requestItem.message.shift_end.toFormat("h:mm a")}
										</Typography>
										{
											<IconButton
												className="edit-icon"
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													setEditingTime({
														requestId: requestItem.id,
														element: e.currentTarget,
													});
												}}
												sx={{
													opacity: 0,
													transition: "opacity 0.2s",
													padding: "2px",
													marginLeft: "4px",
													color: "primary.dark",
												}}
											>
												<EditIcon sx={{ fontSize: "1rem" }} />
											</IconButton>
										}
									</Box>
									<Box
										sx={{
											alignItems: "center",
											color: "primary.dark", // override the isRequest color
											position: "relative",
											"&:hover .edit-icon": {
												opacity: 1,
											},
											display: "flex",
										}}
									>
										<Box sx={{ opacity: 0.5 }}>{formatShiftTotal(requestItem.message.shift_start, requestItem.message.shift_end)}</Box>
										{requestItem.message.shift_type === ShiftType.Day && (
											<WbSunnyIcon
												sx={{
													color: "#FBBB00",
													fontSize: 14,
													opacity: 0.5,
													position: "relative",
													left: "5px",
												}}
											/>
										)}
										{requestItem.message.shift_type === ShiftType.Night && (
											<NightlightIcon
												sx={{
													color: SHIFT_NIGHT_FALLBACK,
													fontSize: 14,
													opacity: 0.5,
													position: "relative",
													left: "5px",
												}}
											/>
										)}
									</Box>
								</Box>

								{editingTime?.requestId === requestItem.id && (
									<EditTimePopover
										role={requestShiftOptionsRole(requestItem.message.roles)}
										jobId={requestItem.job_id}
										startTime={requestItem.message.shift_start}
										endTime={requestItem.message.shift_end}
										onTimeChange={({ newStartTime, newEndTime }) => {
											handleTimePostedShiftChange(requestItem.id, newStartTime, newEndTime);
										}}
										onClose={() => setEditingTime(null)}
										anchorEl={editingTime?.element || null}
										isActual={false}
										maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
									/>
								)}
							</TableCell>
							{/* Location */}
							<TableCell sx={{ width: columnWidths.location.width, minWidth: columnWidths.location.minWidth, textAlign: "left", verticalAlign: "middle" }}>
								<Typography sx={{ fontSize: "13px", color: "#4D5579" }}>{requestItem.location?.trim() || "--"}</Typography>
							</TableCell>

							{/* Number of Patients - Hospital Demo Only */}
							{showHospitalView && (
								<TableCell sx={{ width: columnWidths.patients.width, minWidth: columnWidths.patients.minWidth, textAlign: "center" }}>--</TableCell>
							)}

							{/* Total Workload - Hospital Demo Only */}
							{showHospitalView && (
								<TableCell sx={{ width: columnWidths.workload.width, minWidth: columnWidths.workload.minWidth, textAlign: "center" }}>--</TableCell>
							)}

							{/* Notes (bonus shown here like nurse rows — matches StaffTable header, no separate Wage column) */}
							<TableCell
								sx={{
									width: columnWidths.notes.width,
									minWidth: columnWidths.notes.minWidth,
									textAlign: "left",
									verticalAlign: "middle",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-start" }}>
									<Typography sx={{ fontSize: "14px" }}>{responseCount > 0 ? `${responseCount} Responses` : "No Responses Yet"}</Typography>
									{(requestItem.current_bonus ?? requestItem.bonus) ? (
										<Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>
											(+${requestItem.current_bonus ?? requestItem.bonus})
										</Typography>
									) : null}
								</Box>
							</TableCell>

							{/* Action */}
							<TableCell
								sx={{
									width: columnWidths.action.width,
									minWidth: columnWidths.action.minWidth,
									textAlign: "left",
									verticalAlign: "middle",
								}}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "flex-start",
										alignItems: "center",
										height: "100%",
										width: "100%",
										gap: 0.5,
									}}
								>
									<InitiateRoboCallButton requestId={requestItem.id} variant={"both"} date={requestItem.message.shift_start} />
									<IconButton
										size="small"
										onClick={(event) => handleActionsRequestOpen(event, requestItem as OpenShiftRequest)}
										sx={{
											padding: "2px",
											transition: "opacity 0.1s ease-in-out",
										}}
									>
										<MoreVert sx={{ fontSize: "20px" }} />
									</IconButton>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center" }}>
									{actionRequest && actionRequest.id === requestItem.id && (
										<RequestMenu
											request={actionRequest}
											requestsCount={(actionRequest as OpenShiftRequest).needed_nurses - (actionRequest as OpenShiftRequest).message.accepted_nurses.length}
											date={date}
											shiftType={requestItem.message.shift_type}
											actionsMenuAnchorEl={actionsMenuAnchorEl}
											onMenuClose={() => {
												setActionRequest(null);
												setActionsMenuAnchorEl(null);
											}}
											onMessageClick={handleMessageBroadcastClick}
										/>
									)}
								</Box>
							</TableCell>
						</TableRow>
					</Fragment>
				);
			})}
			{selectedRequest && <NormalizedRequestApprovalDialog open={!!selectedRequest} request={selectedRequest} onClose={handleDialogClose} />}
		</>
	);
};
